<template>
  <section class="account-blocked" aria-labelledby="account-blocked-title">
    <div class="account-blocked__card">
      <div class="account-blocked__icon-wrap" aria-hidden="true">
        <ShieldOff class="account-blocked__icon" :stroke-width="2" />
      </div>
      <h1 id="account-blocked-title" ref="heading" tabindex="-1" class="account-blocked__title">
        {{ copy.title }}
      </h1>
      <p class="account-blocked__message">{{ copy.message }}</p>
      <p v-if="signedInEmail" class="account-blocked__signed-in">
        Signed in as {{ signedInEmail }}
      </p>
      <div class="account-blocked__actions">
        <Button
          v-if="offersDifferentOrganisation"
          variant="primary"
          class="account-blocked__action"
          :disabled="busy"
          @click="signInToDifferentOrganisation"
        >
          Sign in to a different organisation
        </Button>
        <Button
          :variant="offersDifferentOrganisation ? 'outline' : 'primary'"
          class="account-blocked__action"
          :disabled="busy"
          @click="signOut"
        >
          Sign out
        </Button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * The blocked-account screen (US #1814, Task #1818 A24b). LIVE: not behind any switch.
 *
 * Shown by `App.vue` in place of the page, inside the app shell, once `lib/account-access.ts`
 * has seen a 403 `ACCOUNT_DISABLED` or `MEMBERSHIP_REMOVED`, or been told `NO_ORGANISATION` (#2688:
 * not a BFF refusal; the user belongs to no organisation, set by `auth.store`) or
 * `ALL_ORGANISATIONS_DISABLED` (Task #3008: the user is disabled in every organisation they belong
 * to; from the org-less 403 or from `auth.store`). The two single-organisation REFUSALS are about
 * ONE organisation (the token's), so only they offer "Sign in to a different organisation"
 * (Task #2731); `NO_ORGANISATION` and `ALL_ORGANISATIONS_DISABLED` offer sign out only.
 * `ALL_ORGANISATIONS_DISABLED` also names the signed-in account (Task #3008), so the person can
 * tell which account is blocked. It is not a route: the story does not mount any route
 * unconditionally.
 *
 * Both actions reuse existing paths. Sign out is `UserMenu`'s logout. "Sign in to a different
 * organisation" is signing out, which returns to the login page. The login page lists no
 * organisations (#2909); after sign-in, Auth0's own organisation chooser shows only the
 * person's own organisations (`/api/user/organisations` is itself refused while blocked).
 */
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ShieldOff } from 'lucide-vue-next'
import { Button } from '@/components/ui'
import { useAuthStore } from '@/stores/auth.store'
import type { AccountBlock } from '@/lib/account-access'
import {
  ALL_ORGANISATIONS_DISABLED_MESSAGE,
  ALL_ORGANISATIONS_DISABLED_TITLE,
} from '@/constants/all-organisations-disabled'
import { NO_ORGANISATION_MESSAGE, NO_ORGANISATION_TITLE } from '@/constants/no-organisation'

const props = defineProps<{ block: AccountBlock }>()

const router = useRouter()
const authStore = useAuthStore()
const heading = ref<HTMLElement | null>(null)
const busy = ref(false)

/** Only a refusal for ONE organisation (the token's) has a different one to sign in to. */
const offersDifferentOrganisation = computed(
  () => props.block.code === 'ACCOUNT_DISABLED' || props.block.code === 'MEMBERSHIP_REMOVED'
)

const copy = computed(() => {
  if (props.block.code === 'NO_ORGANISATION') {
    return { title: NO_ORGANISATION_TITLE, message: NO_ORGANISATION_MESSAGE }
  }
  if (props.block.code === 'ALL_ORGANISATIONS_DISABLED') {
    return { title: ALL_ORGANISATIONS_DISABLED_TITLE, message: ALL_ORGANISATIONS_DISABLED_MESSAGE }
  }
  return props.block.code === 'ACCOUNT_DISABLED'
    ? {
        title: 'Access disabled',
        message: `Your access to ${props.block.organisationName ?? 'this organisation'} has been disabled. Contact its administrator.`,
      }
    : {
        title: 'Access removed',
        message: `You no longer have access to ${props.block.organisationName ?? 'this organisation'}.`,
      }
})

/** Shown only on ALL_ORGANISATIONS_DISABLED, so the person can tell which account is blocked. */
const signedInEmail = computed(() =>
  props.block.code === 'ALL_ORGANISATIONS_DISABLED' ? (authStore.user?.email ?? null) : null
)

async function signOut() {
  if (busy.value) return
  busy.value = true
  const returnTo = import.meta.env.VITE_AUTH0_LOGOUT_URL || window.location.origin + '/login'
  await authStore.logout({ returnTo })
  await router.push('/login')
}

async function signInToDifferentOrganisation() {
  if (busy.value) return
  await signOut()
}

// Announce the message and put keyboard users one Tab from the actions.
onMounted(async () => {
  await nextTick()
  heading.value?.focus()
})
</script>
